import { useState } from "react";
import { Calendar, MapPin, Users, Clock, ArrowUpRight } from "lucide-react";
import BootcampRegisterDialog from "@/components/site/BootcampRegisterDialog";
import { testIds } from "@/lib/api";
import { useCurrency } from "@/lib/currency";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function Bootcamps({ bootcamps, refresh }) {
  const [selected, setSelected] = useState(null);
  const { format, isUsd } = useCurrency();

  return (
    <section
      id="bootcamps"
      data-testid={testIds.bootcamps.section}
      className="py-24 md:py-32 px-6 md:px-12"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-20">
          <div>
            <div className="overline mb-4">Now Enrolling</div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tighter text-[#2D372B] leading-[1.02]">
              Upcoming <span className="italic text-[#AD6A48]">Bootcamps</span>
            </h2>
          </div>
          <p className="max-w-md text-[#5C665A] leading-relaxed">
            Small cohorts. Real accountability. Curated programming across
            training, wellness, and nutrition.
          </p>
        </div>

        {bootcamps.length === 0 && (
          <div className="text-center py-24 text-[#5C665A]">Loading bootcamps…</div>
        )}

        <div className="grid gap-6 md:gap-8">
          {bootcamps.map((b, idx) => (
            <article
              key={b.id}
              data-testid={testIds.bootcamps.card(b.slug)}
              className="group grid md:grid-cols-12 gap-0 bg-white border border-[#E5E2D9] rounded-3xl overflow-hidden hover:shadow-lg transition-shadow duration-500"
            >
              <div className="md:col-span-5 aspect-[4/3] md:aspect-auto overflow-hidden">
                <img
                  src={b.image_url}
                  alt={b.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>

              <div className="md:col-span-7 p-8 md:p-12 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="font-serif-display italic text-[#AD6A48] text-lg">
                      0{idx + 1}
                    </span>
                    <span className="overline">{b.duration.split("·")[0].trim()}</span>
                  </div>
                  <h3 className="font-serif-display text-3xl md:text-4xl font-medium tracking-tight text-[#2D372B] mb-3">
                    {b.title}
                  </h3>
                  <p className="text-[#5C665A] leading-relaxed mb-6 max-w-2xl">
                    {b.description}
                  </p>

                  <div className="grid sm:grid-cols-2 gap-3 text-sm text-[#5C665A] mb-6">
                    <div className="flex items-center gap-2">
                      <Calendar size={16} className="text-[#89937C]" />
                      Starts {formatDate(b.date)}
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock size={16} className="text-[#89937C]" />
                      {b.duration}
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin size={16} className="text-[#89937C]" />
                      {b.location}
                    </div>
                    <div className="flex items-center gap-2">
                      <Users size={16} className="text-[#89937C]" />
                      {b.spots_remaining} spots left · max {b.spots_total}
                    </div>
                  </div>

                  {b.highlights?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-6">
                      {b.highlights.map((h) => (
                        <span
                          key={h}
                          className="text-xs px-3 py-1.5 rounded-full bg-[#F7F5F0] border border-[#E5E2D9] text-[#5C665A]"
                        >
                          {h}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap items-center justify-between gap-6 pt-6 border-t border-[#E5E2D9]">
                  <div>
                    <div className="overline mb-1">Fee</div>
                    <div className="font-serif-display text-4xl text-[#2D372B]">
                      {format(b.price)}
                    </div>
                    {!isUsd && (
                      <div className="text-xs text-[#89937C] mt-1">≈ ${b.price.toFixed(0)} USD</div>
                    )}
                  </div>
                  <button
                    data-testid={testIds.bootcamps.registerBtn(b.slug)}
                    onClick={() => setSelected(b)}
                    disabled={b.spots_remaining <= 0}
                    className="group/btn inline-flex items-center gap-2 rounded-full px-7 py-3.5 bg-[#89937C] text-white font-medium hover:bg-[#737D67] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {b.spots_remaining <= 0 ? "Fully Booked" : "Register & Pay"}
                    {b.spots_remaining > 0 && (
                      <ArrowUpRight
                        size={18}
                        className="transition-transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5"
                      />
                    )}
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>

      <BootcampRegisterDialog
        bootcamp={selected}
        onClose={() => setSelected(null)}
        onSuccess={() => {
          setSelected(null);
          refresh?.();
        }}
      />
    </section>
  );
}
